import React, {ReactNode, useEffect, useState} from 'react';
import SideBar from './Sidebar';
import {Link, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import routesTitle from "../../__mockData__/routes";
import {BellIcon, SearchIcon} from "../icon";
import {Avatar, Button, Space, Tooltip} from "antd";
import {logout} from "../../redux/slice/accountSlice";
import {LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, InboxOutlined, CloseOutlined} from "@ant-design/icons";
import {COLORS} from "../../designSystem";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../auth/adminPermissions";
import usePanelSize from "./usePanelSize";

interface AdminLayoutProps {
    children: ReactNode;
}

const NAV_RAIL_WIDTH = 80;
const NAV_OPEN_WIDTH = 256;

const readCollapsed = () => {
    try {
        const raw = window.localStorage.getItem("payhive.nav.collapsed");
        // Icons-only is the default: the nav is reference material, not the work.
        return raw === null ? true : JSON.parse(raw);
    } catch {
        return true;
    }
};

export const AdminLayout: React.FC<AdminLayoutProps> = ({children}) => {
    const {avatar, firstName, lastName, permissions, roles} = useSelector((state: RootState) => state.account);
    const dispatch = useDispatch();
    let location = useLocation();
    const canViewLiquidations = hasAdminPermission(permissions, roles, ADMIN_PERMISSIONS.groupSavingsLiquidationView);

    const [navCollapsed, setNavCollapsed] = useState<boolean>(readCollapsed);

    useEffect(() => {
        try {
            window.localStorage.setItem("payhive.nav.collapsed", JSON.stringify(navCollapsed));
        } catch {
            /* Remembering the nav state is not worth throwing over. */
        }
    }, [navCollapsed]);

    // Only the operations panel is draggable — the nav is a fixed rail that toggles.
    const ops = usePanelSize("payhive.ops", {
        defaultWidth: 320,
        min: 240,
        max: 520,
        collapsedWidth: 56,
        defaultCollapsed: true,
        edge: "left",
    });

    return (
        <div className="h-screen flex overflow-y-hidden">
            <div
                className="flex-shrink-0 overflow-y-auto overflow-x-hidden border-r transition-[width] duration-200"
                style={{borderColor: COLORS.cardBorder, width: navCollapsed ? NAV_RAIL_WIDTH : NAV_OPEN_WIDTH}}
            >
                <div className={`flex items-center py-5 ${navCollapsed ? "justify-center" : "justify-between px-4"}`}>
                    {!navCollapsed && (
                        <h2 className="mb-0 font-bold tracking-tight" style={{color: COLORS.brand}}>Payhive Admin</h2>
                    )}
                    <Tooltip title={navCollapsed ? "Expand navigation" : "Collapse navigation"} placement="right">
                        <Button
                            type="text"
                            aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
                            icon={navCollapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
                            onClick={() => setNavCollapsed((current) => !current)}
                        />
                    </Tooltip>
                </div>
                <div className="pb-4">
                    <SideBar collapsed={navCollapsed}/>
                </div>
            </div>

            <div className="flex-1 flex flex-col flex-shrink-0 overflow-x-hidden" style={{background: COLORS.surface}}>
                <div className="flex-shrink-0 h-20 flex justify-between items-center px-10 bg-white border-b"
                     style={{borderColor: COLORS.cardBorder}}>
                    <div>
                        <h1 className="font-bold text-2xl mb-0 tracking-tight" style={{color: COLORS.text}}>
                            {routesTitle[location.pathname] || "Payhive Admin"}
                        </h1>
                    </div>

                    <div className="space-x-8 flex items-center">
                        <SearchIcon/>
                        <BellIcon/>

                        <div className="space-x-3 flex items-center">
                            <Avatar src={avatar} style={{background: COLORS.brandTint, color: COLORS.brand}}>
                                {(firstName || "").charAt(0)}
                            </Avatar>
                            <Space className="cursor-pointer items-center font-medium">
                                {firstName} {lastName}
                            </Space>
                            <Button
                                onClick={() => dispatch(logout())} type="primary"
                                shape="round"
                                icon={<LogoutOutlined/>}
                                size={"large"}
                            />
                        </div>
                    </div>
                </div>
                {children}
            </div>

            {canViewLiquidations && (
                <div className="relative flex-shrink-0 flex" style={{width: ops.width}}>
                    {/* Drag handle. Widened on hover so a 6px target is findable. */}
                    <div
                        {...ops.handleProps}
                        title="Drag to resize"
                        className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize z-10 hover:bg-black/10"
                        style={{background: ops.dragging ? "rgba(0,0,0,0.15)" : "transparent", touchAction: "none"}}
                    />

                    {ops.collapsed ? (
                        <div
                            className="flex h-full w-full flex-col items-center gap-4 py-5"
                            style={{background: COLORS.brand}}
                        >
                            <Tooltip title="Open operations queue" placement="left">
                                <Button
                                    type="text"
                                    aria-label="Open operations queue"
                                    onClick={() => ops.setCollapsed(false)}
                                    icon={<InboxOutlined style={{color: COLORS.white, fontSize: 18}}/>}
                                />
                            </Tooltip>
                        </div>
                    ) : (
                        <div className="h-full w-full overflow-y-auto p-6 text-white" style={{background: COLORS.brand}}>
                            <div className="flex items-start justify-between gap-2">
                                <h2 className="text-white py-4 mb-0">{firstName} {lastName}</h2>
                                <Tooltip title="Collapse" placement="left">
                                    <Button
                                        type="text"
                                        aria-label="Collapse operations queue"
                                        onClick={() => ops.setCollapsed(true)}
                                        icon={<CloseOutlined style={{color: "rgba(255,255,255,0.85)"}}/>}
                                    />
                                </Tooltip>
                            </div>
                            <hr style={{borderColor: "rgba(255,255,255,0.25)"}}/>
                            <div className="py-2">
                                <h4 className="text-white/90 mb-4 mt-4">Operations Queue</h4>

                                <Link to="/requests">
                                    <div className="w-full bg-white shadow-lg p-4 rounded-2xl border"
                                         style={{borderColor: COLORS.cardBorder}}>
                                        <h3 className="mb-1" style={{color: COLORS.brand}}>Liquidations ready</h3>
                                        <span style={{color: COLORS.textMuted}}>Review payout requests and manual approvals</span>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
